import { useLingui } from '@lingui/react/macro'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useUserStore } from '@tshared/stores/user.store'
import { Col, Dropdown, type MenuProps, Row } from 'antd'
import React, { useEffect } from 'react'

import { useWebSignout } from '@/apis/user.api.ts'
import Logo from '@/components/brand/Logo.tsx'
import { SettingOutlined } from '@/icons'
import { Authentication } from '@/rbac'
import { useSupervisorStats } from '@/routes/-features/supervisor-dashboard/hooks'
import {
  CallsVolumeChart,
  HandledRejectedRate,
  InboundCallStatistics,
  KpiCards,
  OperatorStatusPanel,
} from '@/routes/-features/supervisor-dashboard/statistics'
import type { RingItem } from '@/routes/-features/supervisor-dashboard/statistics/InboundCallStatistics'
import type { KpiItem } from '@/routes/-features/supervisor-dashboard/statistics/KpiCards'
import { useNavMenus } from '@/routes/-use-nav-menus.tsx'
import { cn } from '@/utils/cn'

import DateTimeDisplay from './-components/DateTimeDisplay'
import OperatorOverview from './-components/OperatorOverview'

import './-styles.css'

const DASHBOARD_VARS = {
  '--stats-card-bg':
    'linear-gradient(100.44deg, #080c2b 4.74%, #1b2260 94.59%), linear-gradient(0deg, #252736, #252736) #252736',
  '--stats-text': '#ffffff',
  '--stats-text-secondary': '#94a3b8',
  '--stats-bar-track': 'rgba(255, 255, 255, 0.08)',
} as React.CSSProperties

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
  beforeLoad: async () => {
    // check user auth
    const user = useUserStore.getState().user
    if (!user?.accessToken) {
      return redirect({
        to: '/auth/sign-in',
        search: { redirect: '/dashboard' },
      })
    }
    return
  },
})

function DashboardPage() {
  const { t } = useLingui()
  const { metrics, operators, kpiItems, operatorStatuses, inboundStatsItems } = useSupervisorStats({
    operatorSort: false,
  })
  const signOutMutation = useWebSignout()

  useEffect(() => {
    document.title = 'ILOS - Dashboard'
  }, [])

  const navMenus = useNavMenus([], 'wallboard')

  const menuItems: MenuProps['items'] = [
    ...navMenus,
    {
      key: 'sign-out',
      label: t`Sign out`,
      danger: true,
      onClick: () => {
        signOutMutation.mutate()
      },
    },
  ]

  const kpiItemsWithLabels = React.useMemo(() => {
    const labels: Record<string, string> = {
      kpi_totalCallsToday: t`Total Calls Today`,
      kpi_totalCallsLastHour: t`Total Calls Last Hour`,
      kpi_callsInProgress: t`Calls In Progress`,
      kpi_avgResponseTime: t`Avg Response Time`,
      kpi_liveGlobalQueueLength: t`Global Queue`,
    }
    return kpiItems.map((i): KpiItem => ({ ...i, label: labels[i.labelKey] ?? i.labelKey }))
  }, [kpiItems, t])

  const inboundStatsItemsWithLabels = React.useMemo(() => {
    const labels: Record<string, string> = {
      inbound_answerTime60s: t`Answered ≤60s`,
      inbound_answerTime180s: t`Answered ≤180s`,
      inbound_missedRateToday: t`Missed Rate`,
    }
    return inboundStatsItems.map(
      (i): RingItem => ({ ...i, label: labels[i.labelKey] ?? i.labelKey }),
    )
  }, [inboundStatsItems, t])

  return (
    <Authentication require={'arc:wallboard:*'} failMode={'empty'}>
      <main
        style={DASHBOARD_VARS}
        className={cn(
          'dashboard flex h-dvh w-screen flex-col overflow-auto p-4 2xl:p-6',
          'bg-[radial-gradient(59.44%_95.11%_at_50%_58.78%,rgba(22,46,206,0.4)_26.08%,rgba(25,0,83,0.4)_69.78%,rgba(30,20,55,0.4)_100%),linear-gradient(0deg,#31365e,#31365e)]',
        )}
      >
        <h1 className="sr-only">Dashboard</h1>
        <div className="mb-4 flex items-center justify-between">
          <Logo />
          <div className={'flex items-center gap-8'}>
            <DateTimeDisplay />
            <Dropdown menu={{ items: menuItems }} trigger={['hover']}>
              <div className="flex h-10 w-10 items-center justify-center">
                <SettingOutlined className="cursor-pointer text-white!" />
              </div>
            </Dropdown>
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          <Row gutter={20}>
            <Col span={16}>
              <KpiCards items={kpiItemsWithLabels} />
            </Col>
            <Col span={8}>
              <HandledRejectedRate
                handledRate={metrics?.today.rates.handled_rate ?? 0}
                rejectedRate={metrics?.today.rates.rejected_rate ?? 0}
              />
            </Col>
          </Row>
          <div className={'h-5'} />
          <Row gutter={20} className="flex-1">
            <Col span={16}>
              <OperatorOverview operators={operators?.operators ?? []} />
            </Col>
            <Col span={8}>
              <OperatorStatusPanel statuses={operatorStatuses} />
            </Col>
          </Row>
          <div className={'h-5'} />
          <Row gutter={20} className="flex-1">
            <Col span={16}>
              <CallsVolumeChart
                inbound={metrics?.volumes.inbound ?? []}
                outbound={metrics?.volumes.outbound ?? []}
                dark
                fontSize={20}
              />
            </Col>
            <Col span={8}>
              <InboundCallStatistics items={inboundStatsItemsWithLabels} />
            </Col>
          </Row>
        </div>
      </main>
    </Authentication>
  )
}
